/* eslint-disable promise/prefer-await-to-callbacks, promise/no-promise-in-callback */
import { AxiosHeaders, AxiosRequestConfig } from 'axios';
// mantine
import { notifications } from '@mantine/notifications';
import useAppStore from '@/store/app';
// utils
import { privateAxiosInstance } from '@/libs/axios';
import RefreshTokenSingleton from '@/utils/api/RefreshTokenSingleton.ts';

declare module 'axios' {
    interface AxiosRequestConfig {
        noErrorMessage?: boolean;

        skipAuthRetry?: boolean;
    }
}

privateAxiosInstance.interceptors.request.use(
    (config) => {
        (config.headers as AxiosHeaders).set(
            'Authorization',
            `Bearer ${useAppStore.getState().accessToken}`
        );
        return config;
    },
    (error: unknown) => {
        throw error;
    }
);

// Define the structure of a retry queue item
interface RetryQueueItem {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolve: (value?: any) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    reject: (error?: any) => void;
    config: AxiosRequestConfig;
}

// Create a list to hold the request queue
const refreshAndRetryQueue: RetryQueueItem[] = [];

const HTTP_STATUS_UNAUTHORIZED = 401;
const HTTP_STATUS_FORBIDDEN = 403;
const isAuthError = (status: number): boolean =>
    status === HTTP_STATUS_UNAUTHORIZED || status === HTTP_STATUS_FORBIDDEN;

privateAxiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest: AxiosRequestConfig = error.config;
        const { status } = error.response;

        if (!isAuthError(status) && !error.config.noErrorMessage) {
            const reason = error?.response?.data.reason;
            notifications.show({
                color: 'red',
                title: status,
                message: reason,
                autoClose: 20_000
            });
        }

        if (error.response && isAuthError(status)) {
            if (originalRequest.skipAuthRetry) {
                throw error;
            }

            if (!RefreshTokenSingleton.isPending) {
                try {
                    // Refresh the access token
                    await RefreshTokenSingleton.refreshToken();

                    // Retry all requests in the queue with the new token
                    for (const {
                        config,
                        resolve,
                        reject
                    } of refreshAndRetryQueue) {
                        privateAxiosInstance
                            .request(config)
                            .then((response) => {
                                resolve(response);
                            })
                            .catch((error_: unknown) => {
                                reject(error_);
                            });
                    }

                    // Clear the queue
                    refreshAndRetryQueue.length = 0;

                    // Retry the original request
                    return await privateAxiosInstance(originalRequest);
                } catch (refreshError) {
                    useAppStore.getState().logout();
                    throw refreshError;
                }
            }

            // Add the original request to the queue
            return new Promise<void>((resolve, reject) => {
                refreshAndRetryQueue.push({
                    config: originalRequest,
                    resolve,
                    reject
                });
            });
        }

        // Return a Promise rejection if the status code is not 403
        throw error;
    }
);

export { privateAxiosInstance as default } from '@/libs/axios';
